import axios from 'axios';
import { basicAuthConfig } from '../configs';
import { urlMyspreadApi } from '../urls';

/**
 * API CRUD para Mantenedores Generales
 * Maneja operaciones CREATE, READ, UPDATE, DELETE para todos los modelos de configuraciones generales
 */

// ================================
// CONFIGURACIÓN BASE
// ================================

const BASE_URL_SIMPLE = '/mantenedores/crud';
const BASE_URL_DEPENDENCIES = '/mantenedores/crud-dependencies';

/**
 * Función helper para construir endpoint
 */
const buildEndpoint = (baseUrl, path) => {
  return `${urlMyspreadApi.replace(/\/$/, '')}${baseUrl}${path}`;
};

/**
 * Función helper para manejar respuestas de error
 */
const handleApiError = (error, operation, model) => {
  console.error(`Error en ${operation} de ${model}:`, error);
  return {
    success: false,
    error: error.response?.data?.message || `Error al ${operation} ${model}`,
    data: null
  };
};

/**
 * Función helper para manejar respuestas exitosas
 */
const handleApiSuccess = (response, operation, model) => {
  return {
    success: true,
    data: response.data.data || response.data,
    message: response.data.message || `${operation} de ${model} exitoso`,
    total: response.data.total
  };
};

// ================================
// FUNCIONES GENÉRICAS CRUD
// ================================

/**
 * Obtener lista paginada de elementos
 */
export const getElementos = async (user, modelo, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    const baseUrl = getBaseUrl(modelo);
    const endpoint = getEndpoint(modelo);
    
    let url = buildEndpoint(baseUrl, endpoint);
    
    // Construir query parameters
    const query = [];
    if (params.pageSize) query.push(`pageSize=${params.pageSize}`);
    if (params.pageNumber) query.push(`pageNumber=${params.pageNumber}`);
    if (params.searchValue && params.searchValue.length > 0) {
      query.push(`searchValue=${encodeURIComponent(params.searchValue)}`);
    }
    
    if (query.length > 0) {
      url += '?' + query.join('&');
    }
    
    const response = await axios.get(url, config);
    return handleApiSuccess(response, 'Obtención', modelo);
    
  } catch (error) {
    return handleApiError(error, 'obtener', modelo);
  }
};

/**
 * Obtener elemento por ID
 */
export const getElementById = async (user, modelo, id) => {
  try {
    const config = basicAuthConfig(user);
    const baseUrl = getBaseUrl(modelo);
    const endpoint = getEndpoint(modelo);
    
    const url = buildEndpoint(baseUrl, `${endpoint}/${id}`);
    const response = await axios.get(url, config);
    
    return handleApiSuccess(response, 'Obtención', modelo);
    
  } catch (error) {
    return handleApiError(error, 'obtener', modelo);
  }
};

/**
 * Crear nuevo elemento
 */
export const createElement = async (user, modelo, data) => {
  try {
    const config = basicAuthConfig(user);
    const baseUrl = getBaseUrl(modelo);
    const endpoint = getEndpoint(modelo);
    
    const url = buildEndpoint(baseUrl, endpoint);
    const response = await axios.post(url, data, config);
    
    return handleApiSuccess(response, 'Creación', modelo);
    
  } catch (error) {
    return handleApiError(error, 'crear', modelo);
  }
};

/**
 * Actualizar elemento completo (PUT)
 */
export const updateElement = async (user, modelo, id, data) => {
  try {
    const config = basicAuthConfig(user);
    const baseUrl = getBaseUrl(modelo);
    const endpoint = getEndpoint(modelo);
    
    const url = buildEndpoint(baseUrl, `${endpoint}/${id}`);
    const response = await axios.put(url, data, config);
    
    return handleApiSuccess(response, 'Actualización', modelo);
    
  } catch (error) {
    return handleApiError(error, 'actualizar', modelo);
  }
};

/**
 * Actualizar elemento parcial (PATCH)
 */
export const patchElement = async (user, modelo, id, data) => {
  try {
    const config = basicAuthConfig(user);
    const baseUrl = getBaseUrl(modelo);
    const endpoint = getEndpoint(modelo);
    
    const url = buildEndpoint(baseUrl, `${endpoint}/${id}`);
    const response = await axios.patch(url, data, config);
    
    return handleApiSuccess(response, 'Actualización parcial', modelo);
    
  } catch (error) {
    return handleApiError(error, 'actualizar', modelo);
  }
};

/**
 * Eliminar elemento
 */
export const deleteElement = async (user, modelo, id) => {
  try {
    const config = basicAuthConfig(user);
    const baseUrl = getBaseUrl(modelo);
    const endpoint = getEndpoint(modelo);
    
    const url = buildEndpoint(baseUrl, `${endpoint}/${id}`);
    const response = await axios.delete(url, config);
    
    return handleApiSuccess(response, 'Eliminación', modelo);
    
  } catch (error) {
    return handleApiError(error, 'eliminar', modelo);
  }
};

// ================================
// CONFIGURACIÓN DE MODELOS
// ================================

/**
 * Determinar la base URL según el modelo
 */
const getBaseUrl = (modelo) => {
  const modelosSimples = [
    'tms', 'tipos-flota', 'banco', 'tipo-cuenta', 'tipos-documento', 'clientes'
  ];
  
  return modelosSimples.includes(modelo) ? BASE_URL_SIMPLE : BASE_URL_DEPENDENCIES;
};

/**
 * Obtener endpoint específico del modelo
 */
const getEndpoint = (modelo) => {
  const endpoints = {
    // Modelos Simples
    'tms': '/tms',
    'tipo-flota': '/tipos-flota',
    'banco': '/bancos', 
    'tipo-cuenta': '/tipos-cuenta',
    'tipos-documento': '/tipos-documento',
    'clientes': '/clientes',
    
    // Modelos con Dependencias
    'regiones': '/regiones',
    'comunas': '/comunas',
    'servicio-spread': '/servicios-spread',
    'tamano-spread': '/tamanos-spread',
    'local-cliente': '/locales-cliente',
    'tamano-cliente': '/tamanos-cliente',
    'centro-despachos': '/centros-despacho'
  };
  
  return endpoints[modelo] || `/${modelo}`;
};

/**
 * Obtener campos requeridos por modelo
 */
export const getCamposModelo = (modelo) => {
  const campos = {
    'tms': [
      { name: 'tms', label: 'TMS', type: 'text', required: true },
      { name: 'descripcion', label: 'Descripción', type: 'text', required: false }
    ],
    'tipos-flota': [
      { name: 'tipo_flota', label: 'Tipo de Flota', type: 'text', required: true }
    ],
    'banco': [
      { name: 'banco', label: 'Banco', type: 'text', required: true }
    ],
    'tipo-cuenta': [
      { name: 'tipo_cuenta', label: 'Tipo de Cuenta', type: 'text', required: true }
    ],
    'tipos-documento': [
      { name: 'documento', label: 'Documento', type: 'text', required: true },
      { name: 'impuesto', label: 'Impuesto (%)', type: 'number', required: true, min: 0, max: 100, step: 0.01 }
    ],
    'regiones': [
      { name: 'region', label: 'Región', type: 'text', required: true }
    ],
    'comunas': [
      { name: 'comuna', label: 'Comuna', type: 'text', required: true },
      { name: 'region_id', label: 'Región', type: 'select', required: true, options: 'regiones' }
    ],
    'servicio-spread': [
      { name: 'servicio', label: 'Servicio Spread', type: 'text', required: true },
      { name: 'descripcion', label: 'Descripción', type: 'text', required: false }
    ],
    'tamano-spread': [
      { name: 'tamano', label: 'Tamaño Spread', type: 'text', required: true },
      { name: 'descripcion', label: 'Descripción', type: 'text', required: false }
    ],
    'local-cliente': [
      { name: 'local', label: 'Local Cliente', type: 'text', required: true },
      { name: 'cliente_id', label: 'Cliente', type: 'select', required: true, options: 'clientes' },
      { name: 'direccion', label: 'Dirección', type: 'text', required: false },
      { name: 'comuna_id', label: 'Comuna', type: 'select', required: true, options: 'comunas' }
    ],
    'tamano-cliente': [
      { name: 'tamano', label: 'Tamaño Cliente', type: 'text', required: true },
      { name: 'descripcion', label: 'Descripción', type: 'text', required: false }
    ],
    'centro-despachos': [
      { name: 'centroDespacho', label: 'Centro de Despacho', type: 'text', required: true },
      { name: 'comuna_id', label: 'Comuna', type: 'select', required: true, options: 'comunas' }
    ],
    'clientes': [
      { name: 'cliente', label: 'Nombre Cliente', type: 'text', required: true }
    ]
  };
  
  return campos[modelo] || [];
};

/**
 * Obtener configuración de columnas para la tabla
 */
export const getColumnasTabla = (modelo) => {
  const columnas = {
    'tms': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'TMS', accessor: 'tms' },
      { Header: 'Descripción', accessor: 'descripcion' }
    ],
    'tipos-flota': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Tipo de Flota', accessor: 'flota' }
    ],
    'banco': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Banco', accessor: 'banco' }
    ],
    'tipo-cuenta': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Tipo de Cuenta', accessor: 'tipo_cuenta' }
    ],
    'tipos-documento': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Documento', accessor: 'documento' },
      { Header: 'Impuesto (%)', accessor: row => `${row.impuesto.toFixed(2)}%`, sortKey: 'impuesto', filterKey: 'impuesto' }
    ],
    'regiones': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Región', accessor: 'region' }
    ],
    'comunas': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Comuna', accessor: 'comuna' },
      { Header: 'Región', accessor: row => row.region?.region || 'Sin asignar', sortKey: 'region.region', filterKey: 'region.region' }
    ],
    'servicio-spread': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Servicio', accessor: 'servicio' },
      { Header: 'Descripción', accessor: 'descripcion' }
    ],
    'tamano-spread': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Tamaño', accessor: 'tamano' },
      { Header: 'Descripción', accessor: 'descripcion' }
    ],
    'local-cliente': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Local', accessor: 'local' },
      { Header: 'Cliente', accessor: row => row.cliente?.cliente || 'Sin asignar', sortKey: 'cliente.cliente', filterKey: 'cliente.cliente' },
      { Header: 'Comuna', accessor: row => row.comuna?.comuna || 'Sin asignar', sortKey: 'comuna.comuna', filterKey: 'comuna.comuna' },
      { Header: 'Dirección', accessor: 'direccion' },
    ],
    'tamano-cliente': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Tamaño', accessor: 'tamano' },
      { Header: 'Cliente', accessor: row => row.cliente?.cliente || 'Sin asignar', sortKey: 'cliente.cliente', filterKey: 'cliente.cliente' }
    ],
    'centro-despachos': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Centro de Despacho', accessor: 'centroDespacho' },
      { Header: 'Comuna', accessor: row => row.comuna?.comuna || 'Sin asignar', sortKey: 'comuna.comuna', filterKey: 'comuna.comuna' }
    ],
    'clientes': [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Nombre Cliente', accessor: 'cliente' }
    ]
  };
  
  return columnas[modelo] || [];
};

// ================================
// FUNCIONES ESPECÍFICAS POR MODELO
// ================================

// TMS
export const getTMS = (user, params) => getElementos(user, 'tms', params);
export const getTMSById = (user, id) => getElementById(user, 'tms', id);
export const createTMS = (user, data) => createElement(user, 'tms', data);
export const updateTMS = (user, id, data) => updateElement(user, 'tms', id, data);
export const patchTMS = (user, id, data) => patchElement(user, 'tms', id, data);
export const deleteTMS = (user, id) => deleteElement(user, 'tms', id);

// Tipos de Flota
export const getTiposFlotaCrud = (user, params) => getElementos(user, 'tipos-flota', params);
export const getTipoFlotaById = (user, id) => getElementById(user, 'tipos-flota', id);
export const createTipoFlota = (user, data) => createElement(user, 'tipos-flota', data);
export const updateTipoFlota = (user, id, data) => updateElement(user, 'tipos-flota', id, data);
export const patchTipoFlota = (user, id, data) => patchElement(user, 'tipos-flota', id, data);
export const deleteTipoFlota = (user, id) => deleteElement(user, 'tipos-flota', id);

// Bancos
export const getBancos = (user, params) => getElementos(user, 'bancos', params);
export const getBancoById = (user, id) => getElementById(user, 'bancos', id);
export const createBanco = (user, data) => createElement(user, 'bancos', data);
export const updateBanco = (user, id, data) => updateElement(user, 'bancos', id, data);
export const patchBanco = (user, id, data) => patchElement(user, 'bancos', id, data);
export const deleteBanco = (user, id) => deleteElement(user, 'bancos', id);

// Tipos de Cuenta
export const getTiposCuenta = (user, params) => getElementos(user, 'tipos-cuenta', params);
export const getTipoCuentaById = (user, id) => getElementById(user, 'tipos-cuenta', id);
export const createTipoCuenta = (user, data) => createElement(user, 'tipos-cuenta', data);
export const updateTipoCuenta = (user, id, data) => updateElement(user, 'tipos-cuenta', id, data);
export const patchTipoCuenta = (user, id, data) => patchElement(user, 'tipos-cuenta', id, data);
export const deleteTipoCuenta = (user, id) => deleteElement(user, 'tipos-cuenta', id);

// Tipos de Documento
export const getTiposDocumentoCrud = (user, params) => getElementos(user, 'tipos-documento', params);
export const getTipoDocumentoById = (user, id) => getElementById(user, 'tipos-documento', id);
export const createTipoDocumento = (user, data) => createElement(user, 'tipos-documento', data);
export const updateTipoDocumento = (user, id, data) => updateElement(user, 'tipos-documento', id, data);
export const patchTipoDocumento = (user, id, data) => patchElement(user, 'tipos-documento', id, data);
export const deleteTipoDocumento = (user, id) => deleteElement(user, 'tipos-documento', id);

// Regiones
export const getRegiones = (user, params) => getElementos(user, 'regiones', params);
export const getRegionById = (user, id) => getElementById(user, 'regiones', id);
export const createRegion = (user, data) => createElement(user, 'regiones', data);
export const updateRegion = (user, id, data) => updateElement(user, 'regiones', id, data);
export const patchRegion = (user, id, data) => patchElement(user, 'regiones', id, data);
export const deleteRegion = (user, id) => deleteElement(user, 'regiones', id);

// Comunas
export const getComunas = (user, params) => getElementos(user, 'comunas', params);
export const getComunaById = (user, id) => getElementById(user, 'comunas', id);
export const createComuna = (user, data) => createElement(user, 'comunas', data);
export const updateComuna = (user, id, data) => updateElement(user, 'comunas', id, data);
export const patchComuna = (user, id, data) => patchElement(user, 'comunas', id, data);
export const deleteComuna = (user, id) => deleteElement(user, 'comunas', id);

// Clientes
export const getClientes = (user, params) => getElementos(user, 'clientes', params);
export const getClienteById = (user, id) => getElementById(user, 'clientes', id);
export const createClienteCrud = (user, data) => createElement(user, 'clientes', data);
export const updateClienteCrud = (user, id, data) => updateElement(user, 'clientes', id, data);
export const patchClienteCrud = (user, id, data) => patchElement(user, 'clientes', id, data);
export const deleteClienteCrud = (user, id) => deleteElement(user, 'clientes', id);

// Servicios Spread
export const getServiciosSpread = (user, params) => getElementos(user, 'servicios-spread', params);
export const getServicioSpreadById = (user, id) => getElementById(user, 'servicios-spread', id);
export const createServicioSpread = (user, data) => createElement(user, 'servicios-spread', data);
export const updateServicioSpread = (user, id, data) => updateElement(user, 'servicios-spread', id, data);
export const patchServicioSpread = (user, id, data) => patchElement(user, 'servicios-spread', id, data);
export const deleteServicioSpread = (user, id) => deleteElement(user, 'servicios-spread', id);

// Tamaños Spread
export const getTamanosSpread = (user, params) => getElementos(user, 'tamanos-spread', params);
export const getTamanoSpreadById = (user, id) => getElementById(user, 'tamanos-spread', id);
export const createTamanoSpread = (user, data) => createElement(user, 'tamanos-spread', data);
export const updateTamanoSpread = (user, id, data) => updateElement(user, 'tamanos-spread', id, data);
export const patchTamanoSpread = (user, id, data) => patchElement(user, 'tamanos-spread', id, data);
export const deleteTamanoSpread = (user, id) => deleteElement(user, 'tamanos-spread', id);

// Locales Cliente
export const getLocalesCliente = (user, params) => getElementos(user, 'locales-cliente', params);
export const getLocalClienteById = (user, id) => getElementById(user, 'locales-cliente', id);

/**
 * Obtener locales por nombre de cliente
 */
export const getLocalesByCliente = async (user, clienteNombre) => {
  try {
    const config = basicAuthConfig(user);
    const url = buildEndpoint(BASE_URL_DEPENDENCIES, `/locales-cliente?cliente=${encodeURIComponent(clienteNombre)}`);
    const response = await axios.get(url, config);
    
    return handleApiSuccess(response, 'Obtención de locales', 'locales-cliente');
    
  } catch (error) {
    return handleApiError(error, 'obtener locales', 'locales-cliente');
  }
};

export const createLocalCliente = (user, data) => createElement(user, 'locales-cliente', data);
export const updateLocalCliente = (user, id, data) => updateElement(user, 'locales-cliente', id, data);
export const patchLocalCliente = (user, id, data) => patchElement(user, 'locales-cliente', id, data);
export const deleteLocalCliente = (user, id) => deleteElement(user, 'locales-cliente', id);

// Tamaños Cliente
export const getTamanosCliente = (user, params) => getElementos(user, 'tamanos-cliente', params);
export const getTamanoClienteById = (user, id) => getElementById(user, 'tamanos-cliente', id);
export const createTamanoCliente = (user, data) => createElement(user, 'tamanos-cliente', data);
export const updateTamanoCliente = (user, id, data) => updateElement(user, 'tamanos-cliente', id, data);
export const patchTamanoCliente = (user, id, data) => patchElement(user, 'tamanos-cliente', id, data);
export const deleteTamanoCliente = (user, id) => deleteElement(user, 'tamanos-cliente', id);

// Centros de Despacho
export const getCentrosDespachoCrud = (user, params) => getElementos(user, 'centros-despacho', params);
export const getCentroDespachoById = (user, id) => getElementById(user, 'centros-despacho', id);
export const createCentroDespacho = (user, data) => createElement(user, 'centros-despacho', data);
export const updateCentroDespacho = (user, id, data) => updateElement(user, 'centros-despacho', id, data);
export const patchCentroDespacho = (user, id, data) => patchElement(user, 'centros-despacho', id, data);
export const deleteCentroDespacho = (user, id) => deleteElement(user, 'centros-despacho', id);
